
/**
 * Utility functions for image optimization
 */

interface ResponsiveImageProps {
  srcSet?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
}

/**
 * Get responsive image attributes for optimal loading and rendering
 * @param src Image source URL
 * @returns Object with srcSet, sizes, and other optimization attributes
 */
export const getResponsiveImageProps = (src: string): ResponsiveImageProps => {
  // If it's an external image (starts with http) or SVG, we can't generate srcset
  if (src.startsWith('http') || src.endsWith('.svg')) {
    return {
      loading: 'lazy',
      decoding: 'async',
    };
  }

  // For local images, return a responsive srcSet
  return {
    srcSet: `${src} 1x, ${src} 2x`,
    sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    loading: 'lazy',
    decoding: 'async',
  };
};

/**
 * Generate optimized image URL with parameters
 * This function can be expanded to work with image CDNs or services
 */
export const getOptimizedImageUrl = (src: string, width?: number, quality = 80): string => {
  // If using an image CDN like Cloudinary, Imgix, etc.
  // return `https://your-image-cdn.com/${src}?w=${width}&q=${quality}&auto=format`;
  
  // For now, just return the original URL
  return src;
};

/**
 * Check if the image is in a modern format (WebP or AVIF)
 */
export const isModernImageFormat = (src: string): boolean => {
  const lowerCaseSrc = src.toLowerCase();
  return lowerCaseSrc.endsWith('.webp') || lowerCaseSrc.endsWith('.avif');
};

